import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { eventsApi } from '../../api/events';
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_ICONS,
  formatEventDate,
  isUpcoming,
  type EventType,
  type CreateEventRequest,
} from '../../types/event';

export function EventListScreen() {
  const { pushScreen } = useTabNavigationStore();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateEventRequest>({
    name: '',
    event_type: 'competition',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    payment_amount: '',
  });

  const { data: events, isLoading } = useQuery({
    queryKey: ['events', currentAssistantId],
    queryFn: () => eventsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateEventRequest) => eventsApi.create(currentAssistantId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', currentAssistantId] });
      setShowForm(false);
      setForm({ name: '', event_type: 'competition', description: '', location: '', start_date: '', end_date: '', payment_amount: '' });
    },
  });

  const handleSubmit = () => {
    if (!form.name.trim() || !form.start_date) {
      alert('Название и дата начала обязательны');
      return;
    }
    createMutation.mutate({
      ...form,
      description: form.description || undefined,
      location: form.location || undefined,
      end_date: form.end_date || undefined,
      payment_amount: form.payment_amount || undefined,
    });
  };

  const upcomingEvents = (events || []).filter(isUpcoming);
  const pastEvents = (events || []).filter((e) => !isUpcoming(e));

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', color: 'var(--text-hint)', marginBottom: '4px',
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка...</p></div>;
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>События</h1>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Отмена' : '+ Создать'}
        </Button>
      </div>

      {/* Create form */}
      {showForm && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Новое событие</h3>
          <div className="space-y-3">
            <div><label style={labelStyle}>Название *</label><input type="text" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Городской чемпионат" style={inputStyle} /></div>
            <div>
              <label style={labelStyle}>Тип</label>
              <select value={form.event_type} onChange={(e) => setForm((prev) => ({ ...prev, event_type: e.target.value as EventType }))} style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}>
                {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{EVENT_TYPE_ICONS[key as EventType]} {label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <div><label style={labelStyle}>Начало *</label><input type="date" value={form.start_date} onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))} style={inputStyle} /></div>
              <div><label style={labelStyle}>Окончание</label><input type="date" value={form.end_date} onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))} style={inputStyle} /></div>
            </div>
            <div><label style={labelStyle}>Место проведения</label><input type="text" value={form.location} onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))} placeholder="Стадион Олимпийский" style={inputStyle} /></div>
            <div><label style={labelStyle}>Стоимость участия</label><input type="number" value={form.payment_amount} onChange={(e) => setForm((prev) => ({ ...prev, payment_amount: e.target.value }))} placeholder="2000" style={inputStyle} /></div>
            <div><label style={labelStyle}>Описание</label><textarea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Детали события..." rows={2} style={{ ...inputStyle, resize: 'none' }} /></div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={createMutation.isPending || !form.name.trim() || !form.start_date} fullWidth>
                {createMutation.isPending ? 'Создание...' : 'Создать'}
              </Button>
              <Button variant="secondary" onClick={() => setShowForm(false)}>Отмена</Button>
            </div>
            {createMutation.isError && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>{(createMutation.error as Error)?.message || 'Ошибка создания'}</p>
            )}
          </div>
        </Card>
      )}

      {/* Upcoming events */}
      {upcomingEvents.length > 0 && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Предстоящие ({upcomingEvents.length})
          </h3>
          <div className="space-y-2">
            {upcomingEvents.map((event) => (
              <div
                key={event.id}
                onClick={() => pushScreen('event-detail', { eventId: event.id })}
                className="flex items-center justify-between p-3 rounded-xl cursor-pointer active:opacity-60 transition-opacity"
                style={{ backgroundColor: 'var(--bg-primary)' }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{EVENT_TYPE_ICONS[event.event_type]}</span>
                  <div>
                    <h4 className="font-medium" style={{ color: 'var(--text-primary)' }}>{event.name}</h4>
                    <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
                      {formatEventDate(event.start_date)}
                      {event.location && ` \u00b7 ${event.location}`}
                    </p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--accent)' }}>
                  {EVENT_TYPE_LABELS[event.event_type]}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Past events */}
      {pastEvents.length > 0 && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Прошедшие ({pastEvents.length})
          </h3>
          <div className="space-y-2">
            {pastEvents.map((event) => (
              <div
                key={event.id}
                onClick={() => pushScreen('event-detail', { eventId: event.id })}
                className="flex items-center justify-between p-3 rounded-xl cursor-pointer active:opacity-60 transition-opacity opacity-70"
                style={{ backgroundColor: 'var(--bg-primary)' }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{EVENT_TYPE_ICONS[event.event_type]}</span>
                  <div>
                    <h4 className="font-medium" style={{ color: 'var(--text-primary)' }}>{event.name}</h4>
                    <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
                      {formatEventDate(event.start_date)}
                      {event.location && ` \u00b7 ${event.location}`}
                    </p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-hint)' }}>
                  {EVENT_TYPE_LABELS[event.event_type]}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty */}
      {(!events || events.length === 0) && !showForm && (
        <Card>
          <div className="text-center py-8">
            <p className="text-4xl mb-3">📅</p>
            <p className="mb-4" style={{ color: 'var(--text-hint)' }}>Пока нет событий</p>
            <Button onClick={() => setShowForm(true)}>Создать первое событие</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
